/**
 * Invoice Flow Module
 * Handles invoice creation and email sending for customers without payment methods
 * Phase 7: Invoice Generation Infrastructure
 * Phase 9: Payment Links Integration
 */

import { buildPaymentLinks } from '../config/payment-config.js';

/**
 * Create and send an invoice to a customer
 * Called when "Invoice Customer" button is clicked
 * @returns {Promise<boolean>} Success status
 */
export async function invoiceCustomer() {
    console.log('📄 Invoice Customer flow started');

    const customer = window.selectedCustomer || window.adminApp?.selectedCustomer;

    if (!customer || !window.currentServiceKey) {
        alert('Please select a customer and service first');
        return false;
    }

    try {
        // Get the amount to invoice
        let amount = 0;

        // Try wizardTotalPrice first
        const wizardTotalPrice = document.getElementById('wizardTotalPrice');
        if (wizardTotalPrice && wizardTotalPrice.textContent) {
            amount = parseFloat(wizardTotalPrice.textContent.replace('$', '').replace(',', ''));
            console.log('Using wizardTotalPrice:', amount);
        }

        // Fall back to totalCostDisplay
        if (!amount || amount === 0) {
            const totalCostDisplay = document.getElementById('totalCostDisplay');
            if (totalCostDisplay) {
                const displayValue = totalCostDisplay.value || totalCostDisplay.textContent || '0';
                amount = parseFloat(displayValue.replace('$', '').replace(',', ''));
                console.log('Using totalCostDisplay:', amount);
            }
        }

        // Check adminApp finalPrice
        if (window.adminApp && window.adminApp.finalPrice && window.adminApp.finalPrice > 0) {
            amount = window.adminApp.finalPrice;
            console.log('Using adminApp.finalPrice:', amount);
        }

        if (amount <= 0) {
            alert('Invalid invoice amount');
            return false;
        }

        console.log(`📄 Creating invoice for $${amount} for customer ${customer.name}`);

        // Show loading state on button
        const invoiceButton = document.getElementById('chargeButton'); // Same button, changes based on context
        const originalButtonText = invoiceButton?.textContent;
        if (invoiceButton) {
            invoiceButton.disabled = true;
            invoiceButton.textContent = '📄 Creating Invoice...';
        }

        // Collect service and boat details
        let serviceName = window.currentServiceKey.replace(/_/g, ' ');
        if (window.serviceData && window.serviceData[window.currentServiceKey]) {
            serviceName = window.serviceData[window.currentServiceKey].name;
        }

        let boatName = window.selectedBoat?.name ||
                      document.getElementById('wizardBoatName')?.value ||
                      document.getElementById('boatName')?.value || '';

        let boatLength = window.selectedBoat?.length ||
                        document.getElementById('wizardBoatLength')?.value ||
                        document.getElementById('boatLength')?.value || '';

        // Prepare customer, boat, and service details for invoice
        const customerDetails = {
            name: customer.name,
            email: customer.email,
            phone: customer.phone || ''
        };

        const boatDetails = boatName ? {
            name: boatName,
            length: boatLength,
            type: document.querySelector('.boat-type-btn.selected')?.getAttribute('data-value') ||
                  document.querySelector('input[name="wizard_boat_type"]:checked')?.value || '',
            hullType: document.querySelector('.hull-type-btn.selected')?.getAttribute('data-value') ||
                     document.querySelector('input[name="wizard_hull_type"]:checked')?.value || ''
        } : {};

        // Build line items for service details
        const lineItems = [];
        let subtotal = 0;

        // Get base price
        let basePrice = window.wizardBasePrice || 0;
        if (basePrice > 0) {
            lineItems.push({
                name: serviceName,
                quantity: 1,
                price: basePrice,
                total: basePrice
            });
            subtotal += basePrice;
        }

        // Add anodes from cart
        if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
            Object.values(window.anodeCart).forEach(anode => {
                const anodeTotal = (anode.selling_price || anode.list_price || 0) * (anode.quantity || 1);
                lineItems.push({
                    name: anode.name,
                    quantity: anode.quantity || 1,
                    price: anode.selling_price || anode.list_price || 0,
                    total: anodeTotal
                });
                subtotal += anodeTotal;
            });
        }

        // Calculate discounts
        const discounts = [];
        if (subtotal > amount) {
            const discountAmount = subtotal - amount;
            let discountName = 'Discount';

            if (window.adminApp?.priceAdjustment) {
                const adj = window.adminApp.priceAdjustment;
                if (adj.type === 'percent') {
                    discountName = `Discount (${adj.value}%)`;
                } else if (adj.type === 'dollar') {
                    discountName = `Discount ($${adj.value})`;
                }
            }

            discounts.push({
                name: discountName,
                amount: discountAmount
            });
        }

        const serviceDetails = {
            service_name: serviceName,
            service_key: window.currentServiceKey,
            line_items: lineItems,
            subtotal: subtotal,
            discounts: discounts,
            total: amount
        };

        // Determine API base URL
        const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://localhost:3001/api'
            : '/api';

        // Create invoice via API
        console.log('📄 Creating invoice...');
        const invoiceResponse = await fetch(`${API_BASE_URL}/invoices`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                customerId: customer.id,
                serviceId: window.selectedService?.id || null,
                boatId: window.selectedBoat?.id || null,
                amount: amount,
                customerDetails: customerDetails,
                boatDetails: boatDetails,
                serviceDetails: serviceDetails,
                notes: document.getElementById('wizardGeneralNotes')?.value || '',
                dueInDays: 30,
                // Flag to prevent trigger from sending duplicate email
                // Billing sends service_completion email directly
                emailSentByBilling: true
            })
        });

        const invoiceResult = await invoiceResponse.json();

        if (!invoiceResult.success) {
            throw new Error(invoiceResult.error || 'Failed to create invoice');
        }

        const invoice = invoiceResult.invoice;
        console.log('✅ Invoice created:', invoice.invoice_number);

        // Send invoice email with payment links
        console.log('📧 Sending invoice email...');

        // Build payment links using configuration
        const paymentLinks = buildPaymentLinks(amount, invoice.invoice_number);

        // NOTE: The send-email edge function expects amounts in CENTS (like Stripe)
        // The "New Invoice" email from operations service handles the proper customer notification
        // This send-email call appears to be redundant and causes duplicate emails
        // Commenting out to prevent duplicate invoice emails
        // TODO: Remove this code block entirely after verifying operations service handles all invoice emails
        /*
        const emailResponse = await fetch(`${API_BASE_URL}/send-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                type: 'invoice',
                invoiceId: invoice.id,
                customerEmail: customer.email,
                customerName: customer.name,
                amount: Math.round(amount * 100), // Convert dollars to cents for edge function
                invoiceNumber: invoice.invoice_number,
                serviceName: serviceName,
                boatName: boatName,
                boatLength: boatLength,
                dueDate: invoice.due_at,
                paymentLinks: paymentLinks,
                lineItems: lineItems,
                subtotal: subtotal,
                discounts: discounts
            })
        });

        const emailResult = await emailResponse.json();

        if (!emailResult.success) {
            console.error('⚠️ Failed to send invoice email:', emailResult.error);
            // Continue anyway - invoice was created
        } else {
            console.log('✅ Invoice email sent successfully');
        }
        */
        // Send service completion email directly from billing (same as charge flow)
        // This ensures customer gets ONE email with full details + invoice due status
        console.log('📧 Sending service completion email with invoice due status...');

        // Helper functions for email sections
        const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
        const laborPerAnode = 15;

        // Build boat conditions section (Paint, Growth, Thru-Hull)
        const paintCondition = document.getElementById('actualPaintCondition')?.getAttribute('data-value') ||
                             document.getElementById('wizardPaintCondition')?.value || '';
        const growthLevel = document.getElementById('actualGrowthLevel')?.getAttribute('data-value') ||
                          document.getElementById('wizardGrowthLevel')?.value || '';
        const thruHullCondition = document.getElementById('wizardThruHull')?.value || '';

        const boatConditionsSection = (paintCondition || growthLevel || thruHullCondition)
          ? `<tr>
              <td style="padding: 0 40px 30px;">
                <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px; font-weight: 600;">Boat Conditions</h3>
                <table width="100%" cellpadding="8" cellspacing="0">
                  ${paintCondition ? `<tr>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                      <p style="margin: 0; color: #6b7280; font-size: 14px;">Paint Condition</p>
                    </td>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                      <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(paintCondition)}</p>
                    </td>
                  </tr>` : ''}
                  ${growthLevel ? `<tr>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                      <p style="margin: 0; color: #6b7280; font-size: 14px;">Growth Level</p>
                    </td>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                      <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(growthLevel)}</p>
                    </td>
                  </tr>` : ''}
                  ${thruHullCondition ? `<tr>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                      <p style="margin: 0; color: #6b7280; font-size: 14px;">Thru-Hull Condition</p>
                    </td>
                    <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                      <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(thruHullCondition.replace(/_/g, ' '))}</p>
                    </td>
                  </tr>` : ''}
                </table>
              </td>
            </tr>`
          : '';

        // Build anode details section (if any)
        let anodeDetailsSection = '';
        const anodeDetails = [];
        if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
            Object.entries(window.anodeAssignments).forEach(([key, anode]) => {
                anodeDetails.push({
                    location: anode.location || 'unknown',
                    position: anode.position || 'unknown',
                    condition: anode.condition !== undefined && anode.condition !== null ? anode.condition : 100,
                    status: anode.status || 'inspected',
                    name: anode.anode_name || 'Not Selected',
                    price: parseFloat(anode.anode_price) || 0,
                    labor: anode.status === 'replaced' ? laborPerAnode : 0
                });
            });

            if (anodeDetails.length > 0) {
                anodeDetailsSection = `<tr>
                    <td style="padding: 0 40px 30px;">
                      <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Anode Inspection & Replacements</h3>
                      <table width="100%" cellpadding="8" cellspacing="0" style="border: 1px solid #e5e7eb;">
                        <thead>
                          <tr style="background-color: #f9fafb;">
                            <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Location</th>
                            <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Condition</th>
                            <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Status</th>
                            <th style="padding: 12px; text-align: right; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${anodeDetails.map(anode => `
                            <tr>
                              <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.location} - ${anode.position}</td>
                              <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.condition}%</td>
                              <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${capitalizeFirst(anode.status)}</td>
                              <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; color: #111827; font-size: 14px; font-weight: 600;">${anode.status === 'replaced' ? '$' + (anode.price + anode.labor).toFixed(2) : 'N/A'}</td>
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>
                    </td>
                  </tr>`;
            }
        }

        // Build propeller section
        const propeller1 = document.getElementById('wizardPropeller1')?.value || '';
        const propeller2 = document.getElementById('wizardPropeller2')?.value || '';
        const propellerNotes = document.getElementById('wizardPropellerNotes')?.value || '';
        let propellerSection = '';
        if (propeller1 || propeller2) {
            propellerSection = `<tr>
                <td style="padding: 0 40px 30px;">
                  <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Propeller Inspection</h3>
                  <div style="background-color: #f9fafb; padding: 16px; border-radius: 6px;">
                    ${propeller1 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 1:</strong> ${capitalizeFirst(propeller1)}</p>` : ''}
                    ${propeller2 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 2:</strong> ${capitalizeFirst(propeller2)}</p>` : ''}
                    ${propellerNotes ? `<p style="margin: 8px 0 0; padding-top: 8px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;"><strong>Notes:</strong> ${propellerNotes}</p>` : ''}
                  </div>
                </td>
              </tr>`;
        }

        // Build pricing breakdown HTML
        const pricingBreakdownHtml = lineItems.length > 0 ? `<tr>
            <td style="padding: 0 40px 30px;">
              <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px; font-weight: 600;">Service Summary</h3>
              <table width="100%" cellpadding="0" cellspacing="0" style="border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden;">
                ${lineItems.map(item =>
                  `<tr style="border-bottom: 1px solid #e5e7eb;">
                    <td style="padding: 12px 16px; color: #374151; font-size: 14px;">${item.name}</td>
                    <td style="padding: 12px 16px; text-align: right; color: #374151; font-size: 14px;">$${item.total.toFixed(2)}</td>
                  </tr>`
                ).join('')}
                ${discounts.length > 0
                  ? discounts.map(d =>
                      `<tr style="border-bottom: 1px solid #e5e7eb;">
                        <td style="padding: 12px 16px; color: #16a34a; font-size: 14px;">${d.name}</td>
                        <td style="padding: 12px 16px; text-align: right; color: #16a34a; font-size: 14px;">-$${d.amount.toFixed(2)}</td>
                      </tr>`
                    ).join('')
                  : ''}
                <tr style="background-color: #f9fafb;">
                  <td style="padding: 16px; font-weight: 600; font-size: 16px; color: #1e3a8a;">Total</td>
                  <td style="padding: 16px; text-align: right; font-weight: 600; font-size: 16px; color: #1e3a8a;">$${amount.toFixed(2)}</td>
                </tr>
              </table>
            </td>
          </tr>` : '';

        // Build payment status section - INVOICE DUE (amber warning box)
        const paymentStatusSection = `<tr>
            <td style="padding: 0 40px 30px;">
              <div style="background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; padding: 20px; text-align: center;">
                <div style="font-size: 18px; font-weight: 600; color: #92400e;">Invoice Due</div>
                <div style="font-size: 14px; color: #374151; margin-top: 8px;">
                  Amount due: <strong>$${amount.toFixed(2)}</strong>
                </div>
                <div style="font-size: 14px; color: #374151; margin-top: 12px;">
                  To pay, reply to this email or contact us at <a href="mailto:info@sailorskills.com" style="color: #1e3a8a; text-decoration: underline;">info@sailorskills.com</a>
                </div>
              </div>
            </td>
          </tr>`;

        // Send service completion email via send-notification
        try {
            const emailResponse = await fetch('/api/send-notification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    type: 'service_completion',
                    data: {
                        customerEmail: customer.email,
                        customerName: customer.name,
                        boatName: boatName,
                        serviceName: serviceName,
                        serviceDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                        totalHours: document.getElementById('wizardTotalHours')?.textContent || 'N/A',
                        invoiceNumber: invoice.invoice_number,
                        invoiceTotal: '$' + amount.toFixed(2),
                        boatConditionsSection: boatConditionsSection,
                        anodeDetailsSection: anodeDetailsSection,
                        propellerSection: propellerSection,
                        pricingBreakdownHtml: pricingBreakdownHtml,
                        paymentStatusSection: paymentStatusSection
                    },
                    serviceName: 'billing'
                })
            }).then(r => r.json());

            if (emailResponse.error) {
                console.error('❌ Failed to send service completion email:', emailResponse.error);
            } else {
                console.log('✅ Service completion email sent with invoice due status');
            }
        } catch (emailError) {
            console.error('❌ Error sending service completion email:', emailError);
        }

        // Mark button as completed (keep disabled to prevent double-invoicing)
        if (invoiceButton) {
            invoiceButton.disabled = true;
            invoiceButton.textContent = '✅ Invoice Sent';
            invoiceButton.style.background = '#4caf50';
            invoiceButton.style.cursor = 'not-allowed';
        }

        // Show success modal
        showInvoiceSuccessModal(customer, amount, invoice, serviceName, boatName);

        // Save conditions log (similar to charge flow) - with email_sent flag
        await saveConditionsLog(customer, invoice, serviceName, boatName, boatLength, amount, true);

        // Save boat anodes
        if (window.selectedBoat?.id && window.anodeAssignments) {
            console.log('💾 Saving boat anodes...');
            try {
                await window.saveBoatAnodes(window.selectedBoat.id, window.anodeAssignments);
                console.log('✅ Boat anodes saved');
            } catch (error) {
                console.error('⚠️ Error saving boat anodes:', error);
            }
        }

        return true;

    } catch (error) {
        console.error('❌ Error creating invoice:', error);
        alert(`Error creating invoice: ${error.message}`);

        // Reset button
        const invoiceButton = document.getElementById('chargeButton');
        if (invoiceButton) {
            invoiceButton.disabled = false;
            invoiceButton.textContent = '📄 Invoice Customer';
        }

        return false;
    }
}

/**
 * Show success modal after invoice creation
 */
function showInvoiceSuccessModal(customer, amount, invoice, serviceName, boatName) {
    const successModal = document.getElementById('successModal');
    const successContent = document.getElementById('successModalContent');

    if (!successModal || !successContent) {
        console.warn('Success modal not found');
        return;
    }

    successContent.innerHTML = `
        <div style="text-align: center; padding: 30px;">
            <div style="font-size: 72px; margin-bottom: 20px;">📄</div>
            <h2 style="color: #1e3a8a; margin: 0 0 20px 0;">Invoice Sent!</h2>

            <div style="background: white; padding: 25px; border-radius: 8px; margin: 20px 0; text-align: left;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
                    <div>
                        <div style="color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Customer</div>
                        <div style="font-weight: 600;">${customer.name}</div>
                        <div style="color: #888; font-size: 14px;">${customer.email}</div>
                    </div>
                    <div style="text-align: right;">
                        <div style="color: #666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Amount Due</div>
                        <div style="font-size: 32px; font-weight: 700; color: #1e3a8a;">$${amount.toFixed(2)}</div>
                    </div>
                </div>

                <div style="border-top: 1px solid #e0e0e0; padding-top: 15px; font-size: 14px; color: #666;">
                    <div><strong>Invoice Number:</strong> ${invoice.invoice_number}</div>
                    <div style="margin-top: 5px;"><strong>Service:</strong> ${serviceName}</div>
                    ${boatName ? `<div style="margin-top: 5px;"><strong>Boat:</strong> ${boatName}</div>` : ''}
                    <div style="margin-top: 5px;"><strong>Due Date:</strong> ${new Date(invoice.due_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                </div>
            </div>

            <div style="padding: 15px; background: #dbeafe; border: 1px solid #3b82f6; border-radius: 6px; margin: 20px 0; font-size: 14px; color: #1e3a8a;">
                📧 Invoice has been sent to ${customer.email}<br>
                <small style="display: block; margin-top: 8px;">Customer can pay via Stripe, Venmo, or Zelle</small>
            </div>

            <button onclick="window.location.reload()" style="padding: 15px 40px; background: #2196F3; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 16px; font-weight: 600; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
                ← Start New Service
            </button>
        </div>
    `;

    successModal.style.display = 'block';
}

/**
 * Save conditions log for invoice (uses same edge function as charge flow)
 * Fixed: Now writes to service_logs table via edge function instead of service_conditions_log
 * @param {boolean} emailSent - Flag to indicate if email was already sent (prevents trigger duplicate)
 */
async function saveConditionsLog(customer, invoice, serviceName, boatName, boatLength, amount, emailSent = false) {
    try {
        // Ensure we have supabaseClient
        if (!window.supabaseClient) {
            console.error('⚠️ Supabase client not available for conditions save');
            return;
        }

        // Helper to get today's date in local timezone
        const getTodayLocal = () => {
            const now = new Date();
            return now.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
        };

        // Build conditions data matching the charge flow format
        const conditionsData = {
            customer_id: customer.id,
            boat_id: window.selectedBoat?.id || null,
            service_id: null,
            order_id: invoice.invoice_number,
            service_type: window.currentServiceKey,
            service_name: serviceName,
            service_date: window.serviceDate || getTodayLocal(),
            time_in: document.getElementById('wizardStartTime')?.value || window.startTime || null,
            time_out: document.getElementById('wizardEndTime')?.value || window.endTime || null,
            service_time: document.getElementById('wizardStartTime')?.value || window.startTime || new Date().toTimeString().split(' ')[0],
            // Read from slider data-value first (has text labels like "good", "moderate")
            paint_condition_overall: document.getElementById('actualPaintCondition')?.getAttribute('data-value') ||
                                   document.getElementById('wizardPaintCondition')?.value || null,
            growth_level: document.getElementById('actualGrowthLevel')?.getAttribute('data-value') ||
                        document.getElementById('wizardGrowthLevel')?.value || null,
            thru_hull_condition: document.getElementById('wizardThruHull')?.value || null,
            thru_hull_notes: document.getElementById('wizardThruHullNotes')?.value || null,
            // Collect propellers in array format (matching charge flow)
            propellers: (() => {
                const props = [];
                for (let i = 1; i <= 5; i++) {
                    const condition = document.getElementById(`wizardPropeller${i}`)?.value;
                    const notes = document.getElementById(`wizardPropeller${i}Notes`)?.value || '';
                    if (condition && condition !== '' && condition !== 'not_inspected') {
                        props.push({
                            number: i,
                            condition: condition,
                            notes: notes
                        });
                    }
                }
                return props;
            })(),
            propeller_notes: document.getElementById('wizardPropellerNotes')?.value || null,
            notes: document.getElementById('wizardGeneralNotes')?.value || null,
            // Collect anode conditions from assignments and cart
            anode_conditions: (() => {
                const conditions = [];
                if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                    Object.values(window.anodeAssignments).forEach(anode => {
                        conditions.push({
                            location: anode.location,
                            position: anode.position,
                            condition_percent: anode.condition,
                            checked_date: new Date().toISOString()
                        });
                    });
                }
                if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
                    Object.values(window.anodeCart).forEach(anode => {
                        conditions.push({
                            anode_id: anode.id,
                            anode_name: anode.name,
                            location: anode.location,
                            position: anode.position,
                            condition_percent: anode.condition_percent || null,
                            notes: anode.notes || null
                        });
                    });
                }
                return conditions;
            })(),
            // Track installed anodes
            anodes_installed: (() => {
                const installed = [];
                if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                    Object.values(window.anodeAssignments).forEach(anode => {
                        if (anode.status === 'replaced' || anode.status === 'retrieve' || anode.status === 'order') {
                            const location = anode.location || 'unknown';
                            const position = anode.position || 'unknown';
                            const fallbackName = `Anode (${location} - ${position})`;
                            installed.push({
                                anode_id: anode.anode_id || null,
                                anode_name: anode.anode_name || fallbackName,
                                catalog_id: anode.anode_id || null,
                                location: anode.location,
                                position: anode.position,
                                quantity: 1,
                                condition_before: anode.condition,
                                price: anode.anode_price || null,
                                status: anode.status,
                                replaced_on_service: true
                            });
                        }
                    });
                }
                if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
                    Object.values(window.anodeCart).forEach(anode => {
                        if (anode.quantity > 0) {
                            installed.push({
                                anode_id: anode.id,
                                anode_name: anode.name || anode.anode_name || 'Anode (catalog item)',
                                catalog_id: anode.catalog_id,
                                quantity: anode.quantity,
                                location: anode.location,
                                position: anode.position,
                                price: anode.selling_price || anode.list_price
                            });
                        }
                    });
                }
                return installed;
            })(),
            created_by: 'billing_system_invoice',
            invoice_id: invoice.id || null,
            email_sent: emailSent  // Prevent trigger from sending duplicate email
        };

        // Use edge function (same as charge flow) - writes to service_logs table
        const { data: conditionsResult, error: conditionsError } = await window.supabaseClient.functions.invoke('save-conditions', {
            body: conditionsData
        });

        if (conditionsError) {
            console.error('⚠️ Failed to save service log:', conditionsError);
        } else if (conditionsResult && conditionsResult.success) {
            console.log('✅ Service log saved for invoice:', conditionsResult.service_log_id);
        } else {
            console.error('⚠️ Failed to save service log:', conditionsResult?.error);
        }
    } catch (error) {
        console.error('⚠️ Error saving conditions (non-critical):', error);
    }
}

// Export for use in main application
window.invoiceCustomer = invoiceCustomer;

console.log('✅ Invoice flow module loaded');
